/**
 * Deployables: barriers (real blockers in the physics world), turrets (fire on line of sight),
 * drones (orbit and heal the owner), fields (pulse damage and slow), markers (delayed strike).
 */
import { EMPTY_FEATURES } from '../arena/features.js';
import { SIM_DT } from '../constants.js';
import { degToRad, vNormalize } from '../sim/math.js';
import { platformBlockers } from './arena.js';
import { applyDamage, areaPacket } from './damage.js';
import { removeDeployable } from './deployable-damage.js';
import { applyImpulse, refreshEffect } from './effects.js';
import { closestPointOnAxis } from './geometry.js';
import { centerOf, combatantCapsule, secondsToTicks } from './state.js';
import { defOf, emit, mech, nextId } from './tick.js';
import { fireHitscan, splashAt } from './weapons.js';
import { weaponOf } from './catalog.js';
/**
 * Barrier boxes as physics blockers (ids are the deployable ids as strings), followed by the
 * arena's moving platforms at this tick's pose (Phase 8), so every query sees them.
 */
export function blockersOf(state, features = EMPTY_FEATURES) {
    const out = [];
    for (const d of state.deployables) {
        if (d.kind !== 'barrier' || !d.params.halfExtents)
            continue;
        out.push({
            id: String(d.id),
            center: { x: d.position.x, y: d.position.y + d.params.halfExtents.y, z: d.position.z },
            halfExtents: { ...d.params.halfExtents },
            yaw: d.yaw,
        });
    }
    platformBlockers(features, state.tick, out);
    return out;
}
export function spawnDeployable(t, slot, kind, position, yaw, hp, seconds, params) {
    const d = {
        id: nextId(t),
        kind,
        slot,
        position: { ...position },
        yaw,
        hp,
        maxHp: hp,
        ticksLeft: secondsToTicks(seconds),
        params,
        timer: 0,
    };
    t.s.deployables.push(d);
    emit(t, { t: 'deployableSpawn', id: d.id, kind, slot, position: { ...position }, yaw });
    return d;
}
/** Floor height under a point (a ray straight down), or the point's own height if none. */
export function groundBelow(t, point, maxDrop = 60) {
    const hit = t.world.castRayHit({ x: point.x, y: point.y + 0.5, z: point.z }, { x: 0, y: -1, z: 0 }, maxDrop);
    return hit ? point.y + 0.5 - hit.distance : point.y;
}
function stepTurret(t, d) {
    d.timer = Math.max(0, d.timer - SIM_DT);
    const enemy = d.slot === 0 ? 1 : 0;
    const target = mech(t, enemy);
    if (!target.alive || !d.params.weapon)
        return;
    const weapon = weaponOf(t.ctx, d.params.weapon);
    const muzzle = { x: d.position.x, y: d.position.y + 2.2, z: d.position.z };
    const aim = centerOf(target.body, defOf(t, enemy));
    const dx = aim.x - muzzle.x;
    const dy = aim.y - muzzle.y;
    const dz = aim.z - muzzle.z;
    const dist = Math.hypot(dx, dy, dz);
    if (dist > (d.params.range ?? weapon.range.max))
        return;
    if (d.timer > 0)
        return;
    const dir = vNormalize({ x: dx, y: dy, z: dz });
    // Line of sight: the arena between the turret and the target.
    const los = t.world.castRayHit(muzzle, dir, dist);
    if (los && los.distance < dist - defOf(t, enemy).capsule.radius)
        return;
    fireHitscan(t, d.slot, weapon, muzzle, dir, degToRad(weapon.hitscan?.spreadDeg ?? 0), muzzle, {
        countStats: false,
        kind: 'turret',
    });
    d.timer = 1 / weapon.fireRate;
}
function stepDrone(t, d, index) {
    const owner = mech(t, d.slot);
    const def = defOf(t, d.slot);
    const angle = t.s.tick * 0.06 + index * 2.1;
    d.position = {
        x: owner.body.position.x + Math.cos(angle) * 2.6,
        y: owner.body.position.y + def.capsule.height * 0.7,
        z: owner.body.position.z + Math.sin(angle) * 2.6,
    };
    if (owner.alive && d.params.hpPerSecond) {
        owner.hp = Math.min(def.hp, owner.hp + d.params.hpPerSecond * SIM_DT);
    }
}
function stepField(t, d) {
    const pulse = d.params.pulse;
    if (!pulse)
        return;
    d.timer -= SIM_DT;
    if (d.timer > 0)
        return;
    d.timer += pulse.interval;
    const enemy = d.slot === 0 ? 1 : 0;
    const target = mech(t, enemy);
    if (target.alive) {
        const cap = combatantCapsule(target, defOf(t, enemy));
        const q = closestPointOnAxis(d.position, cap);
        const dist = Math.max(0, Math.hypot(q.x - d.position.x, q.y - d.position.y, q.z - d.position.z) - cap.radius);
        if (dist <= pulse.radius) {
            applyDamage(t, areaPacket(t, enemy, d.slot, pulse.damage, 'field', d.position));
            if (pulse.slowScale !== undefined &&
                pulse.slowSeconds !== undefined &&
                !t.mods[enemy].ccImmune) {
                refreshEffect(t, enemy, 'slow', d.slot, secondsToTicks(pulse.slowSeconds), {
                    speedScale: pulse.slowScale,
                });
            }
        }
    }
    emit(t, { t: 'explosion', slot: d.slot, position: { ...d.position }, radius: pulse.radius });
}
function detonateMarker(t, d) {
    const det = d.params.detonate;
    if (!det)
        return;
    splashAt(t, d.slot, d.position, det.radius, det.damage, 0.5, false, 'ability');
    if (det.knockUp > 0) {
        const enemy = d.slot === 0 ? 1 : 0;
        const target = mech(t, enemy);
        if (target.alive) {
            const dist = Math.hypot(target.body.position.x - d.position.x, target.body.position.z - d.position.z);
            if (dist <= det.radius)
                applyImpulse(t, enemy, d.slot, { x: 0, y: det.knockUp, z: 0 });
        }
    }
}
export function stepDeployables(t) {
    let droneIndex = 0;
    for (const d of [...t.s.deployables]) {
        if (!t.s.deployables.includes(d))
            continue;
        d.ticksLeft--;
        switch (d.kind) {
            case 'barrier':
                break;
            case 'turret':
                stepTurret(t, d);
                break;
            case 'drone':
                stepDrone(t, d, droneIndex++);
                break;
            case 'field':
                stepField(t, d);
                break;
            case 'marker':
                if (d.ticksLeft <= 0)
                    detonateMarker(t, d);
                break;
        }
        if (d.ticksLeft <= 0 && t.s.deployables.includes(d))
            removeDeployable(t, d);
    }
}
//# sourceMappingURL=deployables.js.map